import React, { useState } from 'react';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import Select from 'react-select';

const FinancialDataTab = ({ register, errors, watch, setValue, inputClassName, paymentMethods, setPaymentMethods, paymentTerms,paymentAccounts }) => {
  const [showCardModal, setShowCardModal] = useState(false);
  const [showBankModal, setShowBankModal] = useState(false);
  const [newCard, setNewCard] = useState({
    cardNumber: '',
    expiryDate: '',
    cardholderFirstName: '',
    cardholderLastName: '',
    securityCode: '',
    billingAddress: '',
    zipCode: ''
  });
  const [newBankAccount, setNewBankAccount] = useState({
    isPersonal: true,
    isChecking: true,
    routingNumber: '',
    accountNumber: '',
    firstName: '',
    lastName: '',
    billingAddress: '',
    zipCode: ''
  });
 
  // Options for select fields
  const paymentMethodOptions = paymentMethods.map(method => ({ value: method.id, label: method.name }));

  const paymentTermsOptions = paymentTerms.map(term => ({ value: term.id, label: term.name }));

  const discountTypeOptions = [
    { value: '%', label: '%' },
    { value: '$', label: '$' }
  ];

  const creditRatingOptions = [
    { value: 'A+', label: 'A+' },
    { value: 'A', label: 'A' },
    { value: 'B+', label: 'B+' },
    { value: 'B', label: 'B' },
    { value: 'C+', label: 'C+' },
    { value: 'C', label: 'C' },
    { value: 'U', label: 'U' }
  ];

  const laborChargeTypeOptions = [
    { value: 'flat_rate', label: 'Flat Rate' },
    { value: 'hourly', label: 'Hourly' }
  ];

  const customStyles = {
    control: (provided) => ({
      ...provided,
      borderColor: '#D1D5DB',
      '&:hover': { borderColor: '#009efb' },
      boxShadow: 'none',
      '&:focus-within': {
        borderColor: '#009efb',
        boxShadow: '0 0 0 1px #009efb'
      }
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#009efb' : state.isFocused ? '#f3f4f6' : 'white',
      color: state.isSelected ? 'white' : '#374151',
      '&:hover': {
        backgroundColor: state.isSelected ? '#009efb' : '#f3f4f6'
      }
    })
  };

  const handleAddCard = () => {
    if (newCard.cardNumber && newCard.cardholderFirstName && newCard.cardholderLastName) {
      setPaymentMethods([...paymentMethods, { ...newCard, type: 'card', id: Date.now() }]);
      setNewCard({
        cardNumber: '',
        expiryDate: '',
        cardholderFirstName: '',
        cardholderLastName: '',
        securityCode: '',
        billingAddress: '',
        zipCode: ''
      });
      setShowCardModal(false);
    }
  };

  const handleAddBankAccount = () => {
    if (newBankAccount.routingNumber && newBankAccount.accountNumber && newBankAccount.firstName && newBankAccount.lastName) {
      setPaymentMethods([...paymentMethods, { ...newBankAccount, type: 'bank', id: Date.now() }]);
      setNewBankAccount({
        isPersonal: true,
        isChecking: true,
        routingNumber: '',
        accountNumber: '',
        firstName: '',
        lastName: '',
        billingAddress: '',
        zipCode: ''
      });
      setShowBankModal(false);
    }
  };

  const removePaymentMethod = (id) => {
    setPaymentMethods(paymentMethods.filter(method => method.id !== id));
  };

  return (
    <div className="space-y-8">
      {/* Account Payment Terms */}
      <div className="bg-gray-50 p-6 rounded-lg">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Account Payment Terms</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Default Payment Method
            </label>
            <Select
              options={paymentMethodOptions}
              value={paymentMethodOptions.find(option => option.value === watch('defaultPaymentMethod'))}
              onChange={(option) => setValue('defaultPaymentMethod', option?.value || '')}
              styles={customStyles}
              placeholder="Select payment method"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Payment Terms
            </label>
            <Select
              options={paymentTermsOptions}
              value={paymentTermsOptions.find(option => option.value === watch('paymentTerms'))}
              onChange={(option) => setValue('paymentTerms', option?.value || '')}
              styles={customStyles}
              placeholder="Select payment terms"
            />
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Discount
              </label>
              <input
                type="number"
                {...register('discount')}
                className={inputClassName}
                placeholder="0.00"
                step="0.01"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type
              </label>
              <Select
                options={discountTypeOptions}
                value={discountTypeOptions.find(option => option.value === watch('discountType'))}
                onChange={(option) => setValue('discountType', option?.value || '%')}
                styles={customStyles}
                placeholder="%"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Credit Rating
            </label>
            <Select
              options={creditRatingOptions}
              value={creditRatingOptions.find(option => option.value === watch('creditRating'))}
              onChange={(option) => setValue('creditRating', option?.value || '')}
              styles={customStyles}
              placeholder="Select credit rating"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Labor Charge Type
            </label>
            <Select
              options={laborChargeTypeOptions}
              value={laborChargeTypeOptions.find(option => option.value === watch('laborChargeType'))}
              onChange={(option) => setValue('laborChargeType', option?.value || '')}
              styles={customStyles}
              placeholder="Select labor charge type"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Amount per Service Call
            </label>
            <input
              type="number"
              {...register('amountPerServiceCall')}
              className={inputClassName}
              placeholder="0.00"
              step="0.01"
            />
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              {...register('billForDriveTime')}
              className="h-4 w-4 text-[#009efb] focus:ring-[#009efb] border-gray-300 rounded"
            />
            <label className="ml-2 block text-sm text-gray-900">
              Bill For Drive Time?
            </label>
          </div>
        </div>
      </div>

      {/* Payment Methods on File */}
      <div className="bg-gray-50 p-6 rounded-lg">
        <div className="flex justify-between items-center mb-4 overflow-x-auto">
          <h3 className="text-lg font-medium text-gray-900">Payment Methods on File</h3>
          <div className="flex space-x-2">
            <button
              type="button"
              onClick={() => setShowCardModal(true)}
              className="flex items-center px-3 py-1 text-sm text-[#009efb] hover:text-[#0089db]"
            >
              <PlusIcon className="w-4 h-4 mr-1" />
              Add New Card
            </button>
            <button
              type="button"
              onClick={() => setShowBankModal(true)}
              className="flex items-center px-3 py-1 text-sm text-[#009efb] hover:text-[#0089db]"
            >
              <PlusIcon className="w-4 h-4 mr-1" />
              Add New Bank Account
            </button>
          </div>
        </div>
 
        {!paymentAccounts || paymentAccounts.length === 0 ? (
          <p className="text-gray-500 text-center py-4">No payment methods on file</p>
        ) : (
          <div className="space-y-4">
            {paymentAccounts.map((method) => (
              <div key={method.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex justify-between items-center">
                  <div>
                    <h4 className="font-medium text-gray-900">
                      {method.type === 'card' ? 'Credit Card' : 'Bank Account'}
                    </h4>
                    <p className="text-sm text-gray-600">
                      {method.type === 'card' 
                        ? `**** **** **** ${method.cardNumber.slice(-4)} - ${method.cardholderFirstName} ${method.cardholderLastName}`
                        : `${method.isPersonal ? 'Personal' : 'Business'} ${method.isChecking ? 'Checking' : 'Savings'} - ${method.firstName} ${method.lastName}`
                      }
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => removePaymentMethod(method.id)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <TrashIcon className="w-5 h-5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add New Card Modal */}
      {showCardModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Add New Card</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Credit Card Number
                  </label>
                  <input
                    type="text"
                    value={newCard.cardNumber}
                    onChange={(e) => setNewCard({...newCard, cardNumber: e.target.value})}
                    className={inputClassName}
                    placeholder="1234 5678 9012 3456"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Expiry Date
                    </label>
                    <input
                      type="text"
                      value={newCard.expiryDate}
                      onChange={(e) => setNewCard({...newCard, expiryDate: e.target.value})}
                      className={inputClassName}
                      placeholder="MM/YY"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Security Code
                    </label>
                    <input
                      type="text"
                      value={newCard.securityCode}
                      onChange={(e) => setNewCard({...newCard, securityCode: e.target.value})}
                      className={inputClassName}
                      placeholder="123"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      First Name
                    </label>
                    <input
                      type="text"
                      value={newCard.cardholderFirstName}
                      onChange={(e) => setNewCard({...newCard, cardholderFirstName: e.target.value})}
                      className={inputClassName}
                      placeholder="First name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Last Name
                    </label>
                    <input
                      type="text"
                      value={newCard.cardholderLastName}
                      onChange={(e) => setNewCard({...newCard, cardholderLastName: e.target.value})}
                      className={inputClassName}
                      placeholder="Last name"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Billing Street Address
                  </label>
                  <textarea
                    value={newCard.billingAddress}
                    onChange={(e) => setNewCard({...newCard, billingAddress: e.target.value})}
                    className={inputClassName}
                    rows="3"
                    placeholder="Enter billing address"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Zip/Postal Code
                  </label>
                  <input
                    type="text"
                    value={newCard.zipCode}
                    onChange={(e) => setNewCard({...newCard, zipCode: e.target.value})}
                    className={inputClassName}
                    placeholder="Enter zip/postal code"
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowCardModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleAddCard}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#009efb] hover:bg-[#0089db]"
                >
                  Add Card
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add New Bank Account Modal */}
      {showBankModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Add New Bank Account</h3>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Account Type
                    </label>
                    <div className="flex space-x-4">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          checked={newBankAccount.isPersonal}
                          onChange={() => setNewBankAccount({...newBankAccount, isPersonal: true})}
                          className="h-4 w-4 text-[#009efb] focus:ring-[#009efb] border-gray-300"
                        />
                        <span className="ml-2 text-sm text-gray-900">Personal</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          checked={!newBankAccount.isPersonal}
                          onChange={() => setNewBankAccount({...newBankAccount, isPersonal: false})}
                          className="h-4 w-4 text-[#009efb] focus:ring-[#009efb] border-gray-300"
                        />
                        <span className="ml-2 text-sm text-gray-900">Business</span>
                      </label>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Account Type
                    </label>
                    <div className="flex space-x-4">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          checked={newBankAccount.isChecking}
                          onChange={() => setNewBankAccount({...newBankAccount, isChecking: true})}
                          className="h-4 w-4 text-[#009efb] focus:ring-[#009efb] border-gray-300"
                        />
                        <span className="ml-2 text-sm text-gray-900">Checking</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          checked={!newBankAccount.isChecking}
                          onChange={() => setNewBankAccount({...newBankAccount, isChecking: false})}
                          className="h-4 w-4 text-[#009efb] focus:ring-[#009efb] border-gray-300"
                        />
                        <span className="ml-2 text-sm text-gray-900">Savings</span>
                      </label>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Routing Number
                    </label>
                    <input
                      type="text"
                      value={newBankAccount.routingNumber}
                      onChange={(e) => setNewBankAccount({...newBankAccount, routingNumber: e.target.value})}
                      className={inputClassName}
                      placeholder="Enter routing number"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Account Number
                    </label>
                    <input
                      type="text"
                      value={newBankAccount.accountNumber}
                      onChange={(e) => setNewBankAccount({...newBankAccount, accountNumber: e.target.value})}
                      className={inputClassName}
                      placeholder="Enter account number"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      First Name
                    </label>
                    <input
                      type="text"
                      value={newBankAccount.firstName}
                      onChange={(e) => setNewBankAccount({...newBankAccount, firstName: e.target.value})}
                      className={inputClassName}
                      placeholder="First name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Last Name
                    </label>
                    <input
                      type="text"
                      value={newBankAccount.lastName}
                      onChange={(e) => setNewBankAccount({...newBankAccount, lastName: e.target.value})}
                      className={inputClassName}
                      placeholder="Last name"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Billing Street Address
                  </label>
                  <input
                    type="text"
                    value={newBankAccount.billingAddress}
                    onChange={(e) => setNewBankAccount({...newBankAccount, billingAddress: e.target.value})}
                    className={inputClassName}
                    placeholder="Enter billing address"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Zip/Postal Code
                  </label>
                  <input
                    type="text"
                    value={newBankAccount.zipCode}
                    onChange={(e) => setNewBankAccount({...newBankAccount, zipCode: e.target.value})}
                    className={inputClassName}
                    placeholder="Enter zip/postal code"
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowBankModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleAddBankAccount}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#009efb] hover:bg-[#0089db]"
                >
                  Add Bank Account
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FinancialDataTab; 